import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminShell from '../AdminShell';
import { api } from '../../../lib/api';
import { useToast } from '../../../hooks/use-toast';
import { ArrowLeft, ArrowRight, Check, Loader2, Save } from 'lucide-react';
import { STEPS, useAiStream } from './shared';
import { STEP_COMPONENTS } from './steps';

const AdminWorkflow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [wf, setWf] = useState(null);
  const [saving, setSaving] = useState(false);
  const stream = useAiStream();
  const wfRef = useRef(null);
  wfRef.current = wf;

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/workflows/${id}`);
        setWf(res.data);
      } catch (e) {
        toast({ title: 'Workflow not found', variant: 'destructive' });
        navigate('/admin/workflows');
      }
    })();
  }, [id, navigate, toast]);

  const setData = useCallback((key, patch) => {
    setWf((w) => ({ ...w, data: { ...w.data, [key]: { ...(w.data?.[key] || {}), ...patch } } }));
  }, []);
  const setArticle = useCallback((patch) => setWf((w) => ({ ...w, article: { ...w.article, ...patch } })), []);
  const setTopic = useCallback((topic) => setWf((w) => ({ ...w, topic })), []);

  const save = useCallback(async (extra = {}) => {
    const w = { ...wfRef.current, ...extra };
    setSaving(true);
    try {
      const res = await api.put(`/admin/workflows/${id}`, {
        topic: w.topic,
        current_step: w.current_step,
        completed_steps: w.completed_steps,
        data: w.data,
        article: w.article,
      });
      setWf((cur) => ({ ...cur, ...res.data, data: { ...res.data.data, ...cur.data }, article: { ...res.data.article, ...cur.article } }));
      return res.data;
    } finally {
      setSaving(false);
    }
  }, [id]);

  // Save current state, then stream an AI action; persist the result locally.
  const runAi = useCallback(async (action, input = {}, onDelta) => {
    try {
      await save();
      const result = await stream.run(id, action, input, onDelta);
      if (result && typeof result === 'object' && 'content' in result && 'title' in result) {
        setArticle(result);
      }
      return result;
    } catch (err) {
      toast({ title: 'AI step failed', description: err.message, variant: 'destructive' });
      return null;
    }
  }, [id, save, stream, setArticle, toast]);

  const goTo = async (step) => {
    if (stream.running) return;
    try {
      await save({ current_step: step });
      setWf((w) => ({ ...w, current_step: step }));
    } catch (e) {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  const complete = async () => {
    const def = STEP_COMPONENTS[STEPS[wf.current_step - 1].key];
    const error = def.validate?.(wf);
    if (error) {
      toast({ title: 'Not quite ready', description: error, variant: 'destructive' });
      return;
    }
    const completed = Array.from(new Set([...(wf.completed_steps || []), wf.current_step]));
    const next = Math.min(STEPS.length, wf.current_step + 1);
    try {
      await save({ completed_steps: completed, current_step: next });
      setWf((w) => ({ ...w, completed_steps: completed, current_step: next }));
    } catch (e) {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  if (!wf) {
    return (
      <AdminShell wide>
        <div className="p-12 text-center text-ink-3 text-sm">Loading workflow…</div>
      </AdminShell>
    );
  }

  const idx = wf.current_step - 1;
  const step = STEPS[idx];
  const { Component } = STEP_COMPONENTS[step.key];
  const maxReachable = Math.max(wf.current_step, ...(wf.completed_steps || []).map((s) => s + 1));
  const isLast = idx === STEPS.length - 1;

  return (
    <AdminShell wide>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate('/admin/workflows')} className="btn-link" data-testid="workflow-back-btn">
          <ArrowLeft className="w-4 h-4" /> All workflows
        </button>
        <button onClick={() => save().then(() => toast({ title: 'Progress saved' })).catch(() => toast({ title: 'Save failed', variant: 'destructive' }))} disabled={saving} className="text-xs text-ink-2 hover:text-ink inline-flex items-center gap-1" data-testid="workflow-save-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save progress
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px,1fr] gap-6 items-start">
        <aside className="bg-white rounded-3xl border border-[var(--line-2)] p-5" data-testid="workflow-sidebar">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-3 px-3 pt-1 pb-4">Content workflow</div>
          <nav className="space-y-1">
            {STEPS.map((s, i) => {
              const n = i + 1;
              const active = n === wf.current_step;
              const done = (wf.completed_steps || []).includes(n);
              const reachable = n <= maxReachable;
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={!reachable}
                  onClick={() => goTo(n)}
                  data-testid={`workflow-step-${s.key}`}
                  className={`w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-2xl text-sm transition-colors ${
                    active ? 'bg-[var(--ink)] text-white' : reachable ? 'text-ink hover:bg-[var(--bg-alt)]' : 'text-ink-3 cursor-not-allowed'
                  }`}
                >
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium shrink-0 ${
                    active ? 'bg-white/15 text-white' : done ? 'bg-emerald-600 text-white' : 'bg-[var(--bg-alt)] text-ink-3'
                  }`}>
                    {done && !active ? <Check className="w-3.5 h-3.5" /> : n}
                  </span>
                  <span className={active ? 'font-medium' : ''}>{s.title}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="bg-white rounded-3xl border border-[var(--line-2)] p-8 lg:p-10 min-w-0" data-testid="workflow-panel">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-3">Step {wf.current_step} of {STEPS.length}</div>
          <h2 className="text-3xl font-semibold tracking-tight text-ink mt-2">{step.title}</h2>
          <p className="text-sm text-ink-2 mt-2 mb-8 max-w-3xl">{step.blurb}</p>

          <Component
            wf={wf}
            setData={setData}
            setArticle={setArticle}
            setTopic={setTopic}
            runAi={runAi}
            ai={stream}
            save={save}
          />

          {!isLast && (
            <div className="mt-10 pt-6 border-t border-[var(--line-2)] flex items-center justify-between">
              <button
                type="button"
                onClick={() => goTo(wf.current_step - 1)}
                disabled={idx === 0 || stream.running}
                className="btn-link disabled:opacity-40"
                data-testid="workflow-prev-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <button
                type="button"
                onClick={complete}
                disabled={saving || stream.running}
                className="btn-primary disabled:opacity-60"
                data-testid="workflow-complete-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Mark complete &amp; continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
};

export default AdminWorkflow;
